import {
  Workflow,
  Plus,
  Clock,
  GitBranch,
  Play,
  BarChart3,
  Sparkles,
  Settings,
  Zap,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/client/components/ui/button';
import { Card, CardContent } from '@/client/components/ui/card';
import { OnboardingEmptyState } from '@/client/components/ui/empty-state';

export function WorkflowsPage() {
  return (
    <div className="space-y-6 stagger-children relative">
      {/* Decorative background gradient */}
      <div className="absolute inset-0 overflow-hidden opacity-30 pointer-events-none">
        <div className="absolute -top-1/2 -right-1/4 w-[800px] h-[800px] rounded-full bg-gradient-to-br from-indigo-500/20 via-purple-500/10 to-transparent blur-3xl" />
        <div className="absolute -bottom-1/2 -left-1/4 w-[600px] h-[600px] rounded-full bg-gradient-to-tr from-purple-500/20 via-indigo-500/10 to-transparent blur-3xl" />
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 relative">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-3">
            <div className="size-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg shadow-indigo-500/20">
              <Workflow className="size-5 text-white" />
            </div>
            Workflow Automation
          </h1>
          <p className="text-muted-foreground mt-2 text-sm sm:text-base">
            Automate repetitive tasks and connect your tools together
          </p>
        </div>
        <Button
          onClick={() => toast.info('Workflow Automation coming soon!')}
          size="lg"
          className="rounded-xl glow gap-2 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700"
        >
          <Plus className="size-5" />
          Create Workflow
        </Button>
      </div>

      {/* Empty State */}
      <OnboardingEmptyState
        icon={Workflow}
        title="Build your first automation"
        steps={[
          { icon: Zap, text: 'Choose a trigger: form submission, link click, or schedule' },
          { icon: GitBranch, text: 'Add actions: send email, SMS, or update records' },
          { icon: Play, text: 'Activate and let it run automatically' },
        ]}
        action={{
          label: 'Create your first workflow',
          onClick: () => toast.info('Workflow Automation coming soon!'),
          icon: Plus,
        }}
      />

      {/* Coming Soon Banner */}
      <Card className="rounded-2xl border-2 border-dashed border-indigo-300 dark:border-indigo-800 bg-gradient-to-r from-indigo-50/50 to-purple-50/50 dark:from-indigo-950/20 dark:to-purple-950/20">
        <CardContent className="flex flex-col sm:flex-row items-center gap-4 p-6">
          <div className="size-16 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg">
            <Clock className="size-8 text-white" />
          </div>
          <div className="text-center sm:text-left flex-1">
            <h3 className="text-lg font-semibold mb-1">Workflow Automation Coming Soon</h3>
            <p className="text-sm text-muted-foreground">
              We&apos;re building a visual workflow builder that connects all your tools. Trigger
              automations from form submissions, link clicks, or schedules. Send emails, SMS, update
              spreadsheets, and more — all without writing code.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Feature Preview */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card className="rounded-2xl py-0 border-0 bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-950/30 dark:to-purple-950/30">
          <CardContent className="flex items-center gap-4 p-4">
            <div className="size-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg shadow-indigo-500/20">
              <Settings className="size-6 text-white" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground font-medium">Visual</p>
              <p className="text-lg font-semibold">Builder</p>
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-2xl py-0 border-0 bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30">
          <CardContent className="flex items-center gap-4 p-4">
            <div className="size-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-500/20">
              <GitBranch className="size-6 text-white" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground font-medium">Conditional</p>
              <p className="text-lg font-semibold">Logic</p>
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-2xl py-0 border-0 bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/30">
          <CardContent className="flex items-center gap-4 p-4">
            <div className="size-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-lg shadow-emerald-500/20">
              <BarChart3 className="size-6 text-white" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground font-medium">Run History</p>
              <p className="text-lg font-semibold">& Debugging</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Example Workflows */}
      <Card className="rounded-2xl border-0 bg-gradient-to-br from-muted/30 to-muted/10">
        <CardContent className="p-6">
          <h4 className="font-semibold mb-4">Example workflows for Armenian organizations</h4>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              <div className="size-2 rounded-full bg-indigo-500" />
              Auto-send confirmation email when form is submitted
            </div>
            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              <div className="size-2 rounded-full bg-indigo-500" />
              SMS reminder 24 hours before an event
            </div>
            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              <div className="size-2 rounded-full bg-indigo-500" />
              Add new form respondents to a Google Sheet
            </div>
            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              <div className="size-2 rounded-full bg-indigo-500" />
              Welcome email sequence for new subscribers
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Pre-built Templates */}
      <Card className="rounded-2xl border-0 bg-gradient-to-br from-primary/5 to-accent/5">
        <CardContent className="flex items-start gap-4 p-6">
          <div className="size-10 rounded-xl bg-gradient-to-br from-primary to-accent flex items-center justify-center shrink-0">
            <Sparkles className="size-5 text-white" />
          </div>
          <div>
            <h4 className="font-semibold mb-1">Pre-built templates to get started</h4>
            <p className="text-sm text-muted-foreground">
              We&apos;ll include ready-to-use workflow templates for common scenarios: event
              registration follow-ups, volunteer coordination, donation thank-you sequences, and
              more. Just customize and activate.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
